import React, { useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Shield, FileText, AlertCircle, Loader2 } from 'lucide-react';

const ConsentModal = ({ isOpen, onConsentGiven }) => {
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { updateUser } = useContext(AuthContext);

  const handleAcceptConsent = async () => {
    if (!consentAccepted) return;

    try {
      setSubmitting(true);
      const token = sessionStorage.getItem('token');
      
      if (!token) {
        alert('Authentication error. Please log in again.');
        return;
      }
      
      // Update consent in backend
      await axios.patch(`${API}/users/consent`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Fetch updated user data from backend
      const userResponse = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update user in React context and localStorage
      if (updateUser && userResponse.data) {
        updateUser(userResponse.data);
      }

      // Close the modal
      onConsentGiven();
    } catch (error) {
      console.error('Failed to update consent:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to update consent. Please try again.';
      alert(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      // Allow closing the modal
      if (!open) {
        onConsentGiven(); // Close the modal (for testing purposes)
      }
    }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-2xl">
            <Shield className="w-8 h-8 text-blue-600" />
            Privacy Policy & Terms Update
          </DialogTitle>
          <DialogDescription className="text-base text-gray-700">
            We've updated our platform to comply with data protection regulations (GDPR/DPDPA)
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Important Notice */}
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
            <p className="flex items-start gap-2 text-sm text-gray-700">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <span>
                To continue using INSIGHT, please review and accept our updated Privacy Policy and Terms of Service.
              </span>
            </p>
          </div>

          {/* What's New */}
          <div>
            <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              What's New?
            </h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold">✓</span>
                <span><strong>Your Data Rights:</strong> You can now download all your data or delete your account anytime</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold">✓</span>
                <span><strong>Privacy Policy:</strong> Detailed information on how we collect, use, and protect your data</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold">✓</span>
                <span><strong>Terms of Service:</strong> Clear terms governing your use of the platform</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold">✓</span>
                <span><strong>GDPR/DPDPA Compliance:</strong> Full compliance with data protection laws</span>
              </li>
            </ul>
          </div>

          {/* Key Points */}
          <div className="bg-gray-50 p-4 rounded border">
            <h4 className="font-semibold mb-3 text-gray-900">Key Points:</h4>
            <ul className="space-y-2 text-sm text-gray-700">
              <li>• We protect your personal information with industry-standard security</li>
              <li>• Your structured qualitative interpretative summary data is confidential and only shared with your chosen psychologist</li>
              <li>• You can export or delete your data at any time from Account Settings</li>
              <li>• We use AI services (OpenAI, Google Gemini) for Analysis generation</li>
              <li>• INSIGHT is a Structured Qualitative Interpretative Summary Model - NOT a diagnostic Model, for counseling purposes only</li>
            </ul>
          </div>

          {/* Links to Documents */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/privacy-policy" target="_blank" className="flex-1">
              <Button variant="outline" className="w-full">
                <FileText className="w-4 h-4 mr-2" />
                Read Privacy Policy
              </Button>
            </Link>
            <Link to="/terms-of-service" target="_blank" className="flex-1">
              <Button variant="outline" className="w-full">
                <FileText className="w-4 h-4 mr-2" />
                Read Terms of Service
              </Button>
            </Link>
          </div>

          {/* Consent Checkbox */}
          <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <input
              type="checkbox"
              id="modal-consent"
              checked={consentAccepted}
              onChange={(e) => setConsentAccepted(e.target.checked)}
              className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="modal-consent" className="text-sm text-gray-700 leading-relaxed">
              I have read and agree to the{' '}
              <Link to="/privacy-policy" target="_blank" className="text-blue-600 hover:underline font-semibold">
                Privacy Policy
              </Link>
              {' '}and{' '}
              <Link to="/terms-of-service" target="_blank" className="text-blue-600 hover:underline font-semibold">
                Terms of Service
              </Link>
              {' '}of INSIGHT platform. I understand my data rights and how my information will be processed.
            </label>
          </div>

          {/* Accept Button and Skip for Testing */}
          <div className="flex justify-between items-center gap-3">
            <Button 
              variant="ghost"
              onClick={() => {
                // For testing/development - skip consent (not recommended for production)
                if (window.confirm("Skip consent? (Only for testing - user won't have consented)")) {
                  onConsentGiven();
                }
              }}
              className="text-gray-500 hover:text-gray-700"
            >
              Skip (Testing Only)
            </Button>
            <Button 
              onClick={handleAcceptConsent} 
              disabled={!consentAccepted || submitting}
              className="w-full sm:w-auto"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Accepting...
                </>
              ) : (
                <>
                  <Shield className="w-4 h-4 mr-2" />
                  Accept & Continue
                </>
              )}
            </Button>
          </div>

          {/* Notice */}
          <p className="text-xs text-gray-500 text-center">
            You must accept these terms to continue using INSIGHT platform
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConsentModal;
